import { OnInit, Component, Input, inject, TemplateRef, ViewChild } from "@angular/core";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { ModuleType } from "@enums/ModuleType";
import { RendererType } from "@enums/RendererType";
import { RendererConfig } from "@models/workspace/RendererConfig";
import { FormatType } from "@enums/FormatType";
import { FilterConfig } from "@models/workspace/FilterConfig";
import { SentMailService } from "@app/mailmessages/service/sentmail.service";
import { SentNotificationOverviewModel } from "@app/mailmessages/models/sentNotificationOvervieuwModel";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { WorkspaceModulePermissionModel } from "@models/permissions/WorkspaceModulePermissions";
import { firstValueFrom } from "rxjs";
import { WorkspaceComponent } from "@app/shared/workspace/workspace.component";
import { TranslatePipe, injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/mailmessages/i18n/scope";

@Component({
    selector: "uc-sentnotification-overview",
    templateUrl: "./sentnotification-overview.component.html",
    styleUrls: ["./sentnotification-overview.component.scss"],
    imports: [WorkspaceComponent, TranslatePipe],
    viewProviders: [provideTranslationScope(scope)],
})
export class SentNotificationOverviewComponent implements OnInit {
    private _sentMailService = inject(SentMailService);
    private _promptService = inject(CerrixPromptService);
    private _translation = injectTranslation(scope);

    @Input() parentTab: TabModel;
    @ViewChild("contentOnlyNotice") contentOnlyNotice: TemplateRef<unknown>;

    data: Promise<SentNotificationOverviewModel[]>;

    tabID: string;
    moduleType: ModuleType.Admin;
    tab: TabModel;
    // Filled with the translated labels before the data resolves; see ngOnInit.
    headerLookup: any = {};

    dateProps: string[] = ["Date"];
    rendererConfig: RendererConfig[] = [
        {
            textColumn: "Date",
            type: RendererType.Default,
            formatType: FormatType.DateTimeFormat,
        },
        {
            textColumn: "HasError",
            actionColumn: "HasError",
            actionValue: true,
            extraValue: "fas fa-exclamation-triangle",
            type: RendererType.Icon,
            columnWidth: 20,
        },
    ];
    filterConfigs: FilterConfig[] = [];

    permissions: WorkspaceModulePermissionModel = {
        canAdd: false,
        canOpen: true,
        canPublish: false,
        isModuleAdmin: false,
        canSetSystemDefault: false,
    };

    async ngOnInit() {
        this.tab = this.parentTab;
        this.data = firstValueFrom(this._sentMailService
            .getSentMails())
            .then(async (rows) => {
                await this._translation.ready();
                this.setHeaderLabels();
                this.mapHeaders(rows);
                this.tab.showLoader = false;
                return rows;
            });
    }

    private setHeaderLabels() {
        this.headerLookup.ID = this._translation.t("sentOverview.idHeader");
        this.headerLookup.Source = this._translation.t("sentOverview.sourceHeader");
        this.headerLookup.Date = this._translation.t("sentOverview.dateHeader");
        this.headerLookup.Recipients = this._translation.t("sentOverview.recipientsHeader");
        this.headerLookup.Subject = this._translation.t("sentOverview.subjectHeader");
        this.headerLookup.Error = this._translation.t("sentOverview.errorHeader");
        this.headerLookup.HasError = this._translation.t("sentOverview.hasErrorHeader");
    }

    private mapHeaders = (rows) => {
        if (rows && rows.length) {
            Object.getOwnPropertyNames(rows[0]).forEach((propName) => {
                if (!this.headerLookup[propName]) {
                    this.headerLookup[propName] = propName;
                }
            });
        }
        return rows;
    };

    async openMailMessage(openedItem: SentNotificationOverviewModel) {
        await this._translation.ready();
        const prompt = this._promptService.prompt({
            maxWidth: "1200px",
            maxHeight: "1000px",
            data: {
                title: this._translation.t("sentOverview.loadingMail"),
                showLoader: true,
                confirmButton: {
                    show: false,
                },
                cancelButton: {
                    text: this._translation.t("sentOverview.closeButton"),
                },
            },
        });

        this._sentMailService.getSentMailDetail(openedItem.ID).subscribe((mail) => {
            prompt.data.title = this._translation.t("sentOverview.sentMailPreviewTitle");
            prompt.data.showLoader = false;
            prompt.data.fieldResult = mail;
            prompt.data.fields = [
                {
                    prettyName: this._translation.t("sentOverview.recipientsField"),
                    fieldName: "Recipients",
                    fieldType: GenericListFieldType.Text,
                    isReadonly: true,
                },
                {
                    prettyName: this._translation.t("sentOverview.subjectField"),
                    fieldName: "Subject",
                    fieldType: GenericListFieldType.Text,
                    isReadonly: true,
                },
                {
                    prettyName: this._translation.t("sentOverview.errorField"),
                    fieldName: "Error",
                    fieldType: GenericListFieldType.PreBox,
                },
                {
                    prettyName: this._translation.t("sentOverview.messageField"),
                    fieldName: "Body",
                    fieldType: GenericListFieldType.RenderBox,
                },
            ];

            if (!mail.HasError) {
                prompt.data.fields.splice(2, 1);
            }

            prompt.data.customTemplate = this.contentOnlyNotice;
            prompt.data.customTemplateContext = {
                notice: this._translation.t("sentOverview.contentOnlyNotice"),
            };
        });
    }
}
